/**
 * useChatCommands — slash-command + quick-create wrapper callbacks delegating to
 * `chatbox/slash-commands.ts` + `features/chat/lib/object-creation.ts`. Invoked
 * once at a stable position in ChatBox so the inner hooks always run in the same
 * order.
 */
import { useCallback } from 'react';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query/keys';
import type { QueryClient } from '@tanstack/react-query';
import { findModulesByObjectType as findModulesByObjectTypeImpl } from '../utils/module-lookup';
import {
  parseTaskInput,
  parseNoteInput,
  parseContactInfo,
  parseEventInput,
} from '@/features/chat/utils/light-parsers';
import {
  runConversationCommand,
  runConversationsCommand,
  runSearchCommand,
  runCreateCommand,
} from '../components/chatbox/slash-commands';
import { completeObjectCreation as runCompleteObjectCreation } from '@/features/chat/lib/object-creation';
import type { Module } from '@/types/modules';
import type { Conversation } from '@/types/conversations';
import type { ConversationJson } from '@/lib/query-engine/types';

type AddLocalMessages = (
  userContent: string,
  assistantContent: string,
  conversationId?: string,
  metadata?: { routing?: string; durationMs?: number; intent?: string },
) => { userMessageId?: string; assistantMessageId: string };

type CreateObjectType = 'task' | 'note' | 'contact' | 'event';

interface PendingModuleSelection {
  objectType: CreateObjectType;
  parsedData: any;
  modules: Array<{ id: string; label: string }>;
  originalInput: string;
}

export interface UseChatCommandsDeps {
  token: string | null;
  activeConversation: Conversation | null;
  availableModules: Module[];
  conversationJson: ConversationJson | undefined;
  allConversations: Conversation[];
  addLocalMessages: AddLocalMessages;
  queryClient: QueryClient;
  pendingModuleSelection: PendingModuleSelection | null;
  setPendingModuleSelection: (value: PendingModuleSelection | null) => void;
}

export interface UseChatCommandsResult {
  findModulesByObjectType: (objectType: string) => Array<{ id: string; label: string }>;
  completeObjectCreation: (
    objectType: CreateObjectType,
    parsedData: any,
    moduleId: string,
    moduleName: string,
    originalInput: string,
  ) => Promise<void>;
  handleModuleSelection: (moduleId: string, moduleName: string) => void;
  handleConversationCommand: () => void;
  handleConversationsCommand: () => void;
  handleSearchCommand: (query: string) => Promise<void>;
  handleTaskCommand: (input: string) => Promise<void>;
  handleNoteCommand: (input: string) => Promise<void>;
  handleContactCommand: (input: string) => Promise<void>;
  handleEventCommand: (input: string) => Promise<void>;
}

export function useChatCommands(deps: UseChatCommandsDeps): UseChatCommandsResult {
  const {
    token,
    activeConversation,
    availableModules,
    conversationJson,
    allConversations,
    addLocalMessages,
    queryClient,
    pendingModuleSelection,
    setPendingModuleSelection,
  } = deps;

  const findModulesByObjectType = useCallback((objectType: string): Array<{ id: string; label: string }> => {
    return findModulesByObjectTypeImpl(availableModules, objectType);
  }, [availableModules]);

  const completeObjectCreation = useCallback(async (
    objectType: CreateObjectType,
    parsedData: any,
    moduleId: string,
    moduleName: string,
    originalInput: string,
  ) => {
    await runCompleteObjectCreation(objectType, parsedData, moduleId, moduleName, originalInput, {
      token,
      activeConversation,
      addLocalMessages,
      queryClient,
      queryKeys,
      api,
    });
  }, [token, activeConversation, addLocalMessages, queryClient]);

  const handleModuleSelection = useCallback((moduleId: string, moduleName: string) => {
    if (!pendingModuleSelection) return;

    const { objectType, parsedData, originalInput } = pendingModuleSelection;
    setPendingModuleSelection(null);

    completeObjectCreation(objectType, parsedData, moduleId, moduleName, originalInput);
  }, [pendingModuleSelection, completeObjectCreation]);

  const handleConversationCommand = useCallback(() => {
    runConversationCommand({ activeConversation, conversationJson, addLocalMessages });
  }, [activeConversation, conversationJson, addLocalMessages]);

  const handleConversationsCommand = useCallback(() => {
    runConversationsCommand({ allConversations, activeConversation, addLocalMessages });
  }, [allConversations, activeConversation, addLocalMessages]);

  const handleSearchCommand = useCallback(async (query: string) => {
    await runSearchCommand(query, { api, token, activeConversation, addLocalMessages });
  }, [token, activeConversation, addLocalMessages]);

  const handleTaskCommand = useCallback(async (input: string) => {
    await runCreateCommand(input, {
      objectType: 'task',
      commandPrefix: '/task',
      usageHelp: 'Usage: `/task <title>`\n\nExamples:\n- `/task Buy groceries`\n- `/task Call mom tomorrow`\n- `/task urgent: Review PR by Friday`',
      promptQuestionNoun: 'task',
      promptClickNoun: 'task',
      fallbackModuleName: 'Kanban',
      noModuleMessage: 'No task module found in this conversation. Please add a Kanban module first.',
      parse: parseTaskInput,
      findModulesByObjectType,
      completeObjectCreation,
      setPendingModuleSelection,
      addLocalMessages,
      activeConversation,
    });
  }, [activeConversation, addLocalMessages, findModulesByObjectType, completeObjectCreation]);

  const handleNoteCommand = useCallback(async (input: string) => {
    await runCreateCommand(input, {
      objectType: 'note',
      commandPrefix: '/note',
      usageHelp: 'Usage: `/note <title or content>`\n\nExample: `/note Remember to follow up with Sarah`',
      promptQuestionNoun: 'doc',
      promptClickNoun: 'note',
      fallbackModuleName: 'Notes',
      noModuleMessage: 'No Notes module found in this conversation. Please add a Notes module first.',
      parse: parseNoteInput,
      findModulesByObjectType,
      completeObjectCreation,
      setPendingModuleSelection,
      addLocalMessages,
      activeConversation,
    });
  }, [activeConversation, addLocalMessages, findModulesByObjectType, completeObjectCreation]);

  const handleContactCommand = useCallback(async (input: string) => {
    await runCreateCommand(input, {
      objectType: 'contact',
      commandPrefix: '/contact',
      usageHelp: 'Usage: `/contact <name> [email] [phone]`\n\nExamples:\n- `/contact John Smith`\n- `/contact Jane Doe jane@example.com`\n- `/contact Bob 555-123-4567`',
      promptQuestionNoun: 'contact',
      promptClickNoun: 'contact',
      fallbackModuleName: 'Contacts',
      noModuleMessage: 'No contact module found in this conversation. Please add a Contacts module first.',
      parse: parseContactInfo,
      findModulesByObjectType,
      completeObjectCreation,
      setPendingModuleSelection,
      addLocalMessages,
      activeConversation,
    });
  }, [activeConversation, addLocalMessages, findModulesByObjectType, completeObjectCreation]);

  const handleEventCommand = useCallback(async (input: string) => {
    await runCreateCommand(input, {
      objectType: 'event',
      commandPrefix: '/event',
      usageHelp: 'Usage: `/event <title> [date/time]`\n\nExamples:\n- `/event Team standup tomorrow at 9am`\n- `/event Dentist appointment Friday 2pm`\n- `/event Birthday party next Saturday`',
      promptQuestionNoun: 'event',
      promptClickNoun: 'event',
      fallbackModuleName: 'Events',
      noModuleMessage: 'No event module found in this conversation. Please add an Events module first.',
      parse: parseEventInput,
      findModulesByObjectType,
      completeObjectCreation,
      setPendingModuleSelection,
      addLocalMessages,
      activeConversation,
    });
  }, [activeConversation, addLocalMessages, findModulesByObjectType, completeObjectCreation]);

  return {
    findModulesByObjectType,
    completeObjectCreation,
    handleModuleSelection,
    handleConversationCommand,
    handleConversationsCommand,
    handleSearchCommand,
    handleTaskCommand,
    handleNoteCommand,
    handleContactCommand,
    handleEventCommand,
  };
}
